import React, { useCallback, useEffect, useState } from 'react';

import { RiPencilFill } from 'react-icons/ri';

import { Card, Checkbox, FormControl, FormControlLabel, TextField } from '@mui/material';

import SkeletonComponent from '../components/SkeletonComponent';
import * as S from '../styles';
import { SavedSubmission } from '../types/formTypes';
import { useSubmitForm } from '../useSubmitForm';
import Button from 'components/Button';
import * as I from 'components/DynamicFormGroup/types/dynamicFormGroupTypes';
import Text from 'components/Text';
import { useEditStatus } from 'global/EditStatusContext';
import { useUser } from 'global/UserContext';
import { useDebounce } from 'hooks/useDebounce';

interface CheckboxFieldProps {
  field: I.CheckboxField & { submission?: SavedSubmission };
}

const CheckboxField: React.FC<CheckboxFieldProps> = ({ field }) => {
  const { user } = useUser();
  const { setEditStatus } = useEditStatus();
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [otherText, setOtherText] = useState<Record<string, string>>({});
  const [isOtherReadOnly, setIsOtherReadOnly] = useState<Record<string, boolean>>({});
  const debouncedFormData = useDebounce(formData, 500);
  const debouncedOtherText = useDebounce(otherText, 500);
  const [prevLoading, setPrevLoading] = useState<boolean>(true);
  const [prevError, setPrevError] = useState<string | null>(null);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const { submitForm, loading: submissionLoading, error: submissionError } = useSubmitForm();

  const user_id = user?.id;

  useEffect(() => {
    const pHold = field.extras?.placeholders || [];
    setPlaceholders(pHold);
    // Initialize form data from submission instead of API call
    if (field.submission && field.submission.values) {
      const savedValues = field.submission.values;
      const otherOption = field.options.find(o => o.option_label === 'Outro');

      savedValues.forEach(savedValue => {
        const optionId = savedValue?.option_id;
        const value = savedValue?.value || '';
        if (optionId) {
          setFormData(prev => ({ ...prev, [optionId]: value }));
          if (otherOption?.id === optionId) {
            setOtherText(prev => ({ ...prev, [optionId]: value }));
          }
        }
      });
    }
    setPrevLoading(false);
  }, [field.submission]);

  const submitData = useCallback(async () => {
    if (!isChanged) return;

    const submissionData: { values: { option_id: string; value: string }[] } = {
      values: Object.keys(formData).map(optionId => ({
        option_id: optionId,
        value: formData[optionId],
      })),
    };
    try {
      await submitForm(field.id, submissionData);
      setIsChanged(false);
    } catch (error) {
      console.error('Error submitting data:', error);
    }
  }, [formData, isChanged]);

  useEffect(() => {
    if (isChanged) {
      submitData();
    }
  }, [debouncedFormData, debouncedOtherText, submitData]);

  const handleCheckboxChange = (optionId: string, optionLabel: string, checked: boolean) => {
    setEditStatus(true, optionLabel);
    setIsChanged(true);
    setFormData(prevData => {
      const updatedData = { ...prevData };

      if (checked) {
        updatedData[optionId] = optionLabel;
      } else {
        delete updatedData[optionId];
      }

      return updatedData;
    });
  };

  const handleOtherCheckboxChange = (optionId: string, checked: boolean) => {
    setEditStatus(true, 'Outro');
    setIsChanged(true);
    setFormData(prevData => {
      const updatedData = { ...prevData };
      if (checked) {
        updatedData[optionId] = 'Outro';
        setIsOtherReadOnly(prevState => ({
          ...prevState,
          [optionId]: false,
        }));
      } else {
        delete updatedData[optionId];
        setOtherText(prevText => ({ ...prevText, [optionId]: '' }));
      }
      return updatedData;
    });
  };

  const handleOtherTextChange = (optionId: string, value: string) => {
    setOtherText(prev => ({ ...prev, [optionId]: value }));
  };

  const handleBlur = (optionId: string) => {
    setIsChanged(true);
    setFormData(prevData => ({
      ...prevData,
      [optionId]: otherText[optionId],
    }));
  };

  if (prevLoading) {
    return <SkeletonComponent />;
  }

  if (prevError) {
    return <div>Error loading data</div>;
  }

  return (
    <Card key={field.id}>
      <Text color="textPrimary" size="medium" bold>
        {field.label}
      </Text>
      {field.description}
      <Text color="textPrimary" opacity={0.5} className="checkboxInfo">
        É posssível selecionar mais de um item.
      </Text>
      <FormControl component="fieldset">
        {field.options.map(option => {
          const isOtherOption = option.option_label === 'Outro';
          return (
            <S.StyledFormControlLabel key={option.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData[option.id]}
                    onChange={e => {
                      const checked = (e.target as HTMLInputElement).checked;

                      if (isOtherOption) {
                        handleOtherCheckboxChange(option.id, checked);
                      } else {
                        handleCheckboxChange(option.id, option.option_label, checked);
                      }
                    }}
                  />
                }
                label={option.option_label}
                sx={{
                  alignItems: 'flex-start',
                  marginTop: '8px',
                }}
              />

              {isOtherOption && !!formData[option.id] && (
                <S.Specify>
                  <TextField
                    placeholder="Especifique"
                    value={otherText[option.id] || ''}
                    onChange={e => handleOtherTextChange(option.id, e.target.value)}
                    variant="standard"
                    InputProps={{
                      readOnly: isOtherReadOnly[option.id],
                    }}
                    onBlur={() => handleBlur(option.id)}
                  />

                  {isOtherReadOnly[option.id] && (
                    <Button
                      icon={<RiPencilFill />}
                      type="link"
                      size="small"
                      onClick={() =>
                        setIsOtherReadOnly(prevState => ({
                          ...prevState,
                          [option.id]: false,
                        }))
                      }
                    >
                      Editar
                    </Button>
                  )}
                </S.Specify>
              )}
            </S.StyledFormControlLabel>
          );
        })}
      </FormControl>
    </Card>
  );
};

export default CheckboxField;
